import {
  Create, Datagrid, Edit, EditButton, FunctionField, List, SimpleForm, TextField, TextInput,
  TopToolbar, required, useRecordContext
} from 'react-admin';
import { Alert, Card, CardContent, Divider, Link as MuiLink, Stack, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { BackLink } from './back';

/**
 * The manufacturers whose products the shop sells.
 *
 * They live in the same table as customer organisations, told apart by `type`,
 * but they are a different thing entirely: 32 producers against 1,424
 * customers, and a producer has no people, no orders and no tax details worth
 * keeping - it exists so a product can say who made it and so the storefront
 * can offer "tonery podľa výrobcov".
 *
 * That is why this is its own screen rather than a filter on the customer
 * list: the two share a table and nothing else.
 */

interface ProducerProduct {
  id: number;
  code: string | null;
  name: string | null;
}

interface Producer {
  id: number;
  name: string;
  acronym: string | null;
  slug: string;
  products?: {
    printers: { total: number; items: ProducerProduct[] };
    others: { total: number; items: ProducerProduct[] };
  };
}

export function ProducerList() {
  return (
    <List
      title="Výrobcovia"
      perPage={50}
      sort={{ field: 'name', order: 'ASC' }}
      exporter={false}
    >
      <Datagrid rowClick="edit" bulkActionButtons={false}>
        <TextField source="name" label="Názov" />
        <TextField source="acronym" label="Skratka" emptyText="—" />
        <FunctionField label="Adresa" render={(record: Producer) => `/producer/${record.slug}`} />
        <EditButton label="Upraviť" />
      </Datagrid>
    </List>
  );
}

function ProducerNotice() {
  const record = useRecordContext<Producer>();
  if (record == null) return null;

  return (
    <Alert severity="info" variant="outlined" sx={{ mb: 2, width: '100%' }}>
      Výrobca sa priraďuje k produktu na karte produktu, v záložke „Zaradenie“. Premenovanie sa
      prejaví všade, kde sa výrobca zobrazuje.
    </Alert>
  );
}

export function ProducerEdit() {
  return (
    <Edit
      title="Výrobca"
      mutationMode="pessimistic"
      redirect="list"
      actions={<TopToolbar sx={{ justifyContent: 'flex-start' }}><BackLink /></TopToolbar>}
    >
      {/* No IČO, DIČ or IČ DPH: a producer is a brand whose products the shop
          resells, not a company it invoices, and those fields would sit empty
          on all 32 of them. They stay on the customer organisation form, where
          they are the point. */}
      <SimpleForm>
        <ProducerNotice />
        <TextInput source="name" label="Názov" validate={required()} fullWidth />
        <TextInput source="acronym" label="Skratka" helperText="Voliteľná kratšia podoba názvu." />
        <ProducerCatalogue />
      </SimpleForm>
    </Edit>
  );
}

/**
 * What this producer makes.
 *
 * Printers first because that is how the shop is searched - somebody looks up
 * a printer, then what fits it. A preview with a way through to the full list
 * rather than the catalogue itself: HP has 2,664 products, and the products
 * screen already filters by producer and paginates properly.
 */
function ProducerCatalogue() {
  const record = useRecordContext<Producer>();
  if (record?.products == null) return null;

  const { printers, others } = record.products;

  return (
    <Stack spacing={2} sx={{ width: '100%', mt: 2 }}>
      <ProductGroup
        title="Tlačiarne"
        group={printers}
        producerId={record.id}
        empty="Tento výrobca nemá evidované žiadne tlačiarne."
      />
      <ProductGroup
        title="Náplne a ostatné produkty"
        group={others}
        producerId={record.id}
        empty="Tento výrobca nemá evidované žiadne ďalšie produkty."
      />
    </Stack>
  );
}

function ProductGroup({ title, group, producerId, empty }: {
  title: string;
  group: { total: number; items: ProducerProduct[] };
  producerId: number;
  empty: string;
}) {
  // The products screen keeps its filters in the URL, so this lands on exactly
  // this producer's list rather than an unfiltered one.
  const allUrl = `/products?filter=${encodeURIComponent(JSON.stringify({ organization_id: producerId }))}`;

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline">
          <Typography variant="overline" color="text.secondary">
            {title} ({group.total})
          </Typography>
          {group.total > 0 && (
            <MuiLink component={Link} to={allUrl} variant="caption">
              zobraziť všetky
            </MuiLink>
          )}
        </Stack>

        {group.items.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{empty}</Typography>
        ) : (
          <Stack divider={<Divider flexItem />} sx={{ mt: 1 }}>
            {group.items.map(product => (
              <Stack key={product.id} direction="row" spacing={2} sx={{ py: 0.5 }}>
                <MuiLink component={Link} to={`/products/${product.id}`} variant="body2">
                  {product.name ?? product.code ?? `#${product.id}`}
                </MuiLink>
                {product.code != null && product.code !== product.name && (
                  <Typography variant="caption" color="text.secondary">{product.code}</Typography>
                )}
              </Stack>
            ))}
            {group.total > group.items.length && (
              <Typography variant="caption" color="text.secondary" sx={{ pt: 1 }}>
                Zobrazených {group.items.length} z {group.total}.
              </Typography>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

export function ProducerCreate() {
  return (
    <Create title="Nový výrobca" redirect="list">
      <SimpleForm>
        <Alert severity="info" variant="outlined" sx={{ mb: 2, width: '100%' }}>
          Adresa výrobcu na webe sa vytvorí z názvu automaticky. Produkty k nemu priradíte na karte
          produktu.
        </Alert>
        <TextInput source="name" label="Názov" validate={required()} fullWidth />
      </SimpleForm>
    </Create>
  );
}
